import { showToast } from "@/lib/utils/toast";
import { targetSetupService } from "../services/target-setup.services";
import { targetSetupStore } from "../stores/target-setup.store";
import { targetSetupUIStore } from "../stores/target-setup-ui.store";
import { organizationService } from "@/features/organization";
import { companyService } from "@/features/company/services/company.service";
import { authSessionStore } from "@/features/auth/stores/auth-session.store";
import { companyStore } from "@/features/company/stores/company.store";
import {
  buildDisclosureYearKey,
  getTargetValueValidationMessage,
} from "../domains/targetSetup";
import {
  disambiguateOrgDisplayNames,
  flattenOrgTree,
} from "@/features/organization/utils/organization.helpers";
import {
  isSuperAdminRole,
  normalizeOrgId,
} from "@/features/esg/utils/org-scope.utils";
import {
  stripGroupNodes,
  filterOrgTreeByIds,
} from "@/features/data-entry/utils/orgTree.helpers";
import { buildAssignedFrameworks } from "@/lib/utils/frameworks.utils";

const EXPORT_MODE_OPTIONS = [
  { value: "filled", label: "Filled Targets" },
  { value: "empty", label: "Unfilled Targets" },
  { value: "mixed", label: "All Targets" },
];

const getCompanyId = (company) =>
  company?.id ?? company?._id ?? company?.companyId ?? null;

const resolveCompanyPayload = (response) => {
  if (!response || typeof response !== "object") return response;
  const payload = "data" in response ? response.data : response;
  return payload?.company ?? payload?.data?.company ?? payload;
};

const inferTargetFrequencyFromItem = (item) => {
  if (item?.targetFrequency === "monthly") return "monthly";
  if (item?.targetFrequency === "yearly") return "yearly";
  return null;
};

const resolveDisclosureTargetFrequency = (items = [], fallback = "yearly") => {
  for (const item of items) {
    const frequency = inferTargetFrequencyFromItem(item);
    if (frequency) return frequency;
  }
  return fallback === "monthly" ? "monthly" : "yearly";
};

const buildFallbackTargetSetupFileName = ({
  year,
  exportMode = "filled",
} = {}) => {
  const yearToken = String(year || new Date().getFullYear()).trim() || "export";
  const modeToken = EXPORT_MODE_OPTIONS.find(
    (v) => v.value == exportMode,
  ).label;
  return `Target_Setup_${yearToken}_${modeToken}.xlsx`;
};

let orgListPromise = null;
let orgListRequestVersion = 0;

export const resetOrgListLoader = () => {
  orgListPromise = null;
  orgListRequestVersion += 1;
};

export const loadCompanyTargetFrequency = async () => {
  const companyId = authSessionStore.user?.companyId;
  targetSetupStore.setTargetFrequencyLoaded(false);
  if (!companyId) {
    targetSetupStore.setTargetFrequency("yearly");
    return "yearly";
  }

  // Use cached company data first to avoid an extra API call
  const cached = companyStore.company;
  if (cached && String(getCompanyId(cached) || "") === String(companyId)) {
    const frequency =
      cached.targetFrequency === "monthly" ? "monthly" : "yearly";
    targetSetupStore.setTargetFrequency(frequency);
    return frequency;
  }

  try {
    const response = await companyService.getCompany(companyId);
    const company = resolveCompanyPayload(response);
    const frequency =
      company?.targetFrequency === "monthly" ? "monthly" : "yearly";
    targetSetupStore.setTargetFrequency(frequency);
    return frequency;
  } catch {
    targetSetupStore.setTargetFrequency("yearly");
    return "yearly";
  }
};

export const updateCompanyTargetFrequency = async (frequency) => {
  const companyId = authSessionStore.user?.companyId;
  if (!companyId) return;

  const previous = targetSetupStore.targetFrequency;
  targetSetupStore.setTargetFrequency(frequency);

  try {
    await companyService.updateCompany(companyId, {
      targetFrequency: frequency,
    });
    showToast.success("Target frequency updated");
  } catch (err) {
    targetSetupStore.setTargetFrequency(previous);
    showToast.error(err?.message || "Failed to update target frequency");
  }
};

export const loadDisclosures = async ({ initFrameworks = false } = {}) => {
  const { hydrateDisclosureData } = targetSetupStore;
  const { setLoadingDisclosures } = targetSetupUIStore;

  setLoadingDisclosures(true);
  try {
    // Load the full assigned disclosure list once. Page-level search stays
    // client-side so typing does not refetch the same dataset repeatedly.
    const data = await targetSetupService.getMasterDataDisclosures();
    const boundary = data?.reportingBoundary ?? null;
    const disclosures = data?.disclosures ?? [];
    hydrateDisclosureData({
      disclosures,
      reportingBoundary: boundary,
      frameworks: initFrameworks
        ? buildAssignedFrameworks({
            disclosures,
            frameworks: boundary?.frameworks,
          })
        : undefined,
    });
  } catch {
    showToast.error("Failed to load disclosures");
  } finally {
    setLoadingDisclosures(false);
  }
};

export const loadOrgList = async () => {
  const { orgTreeLoaded, setOrgScope } = targetSetupStore;
  const { setLoadingOrgTree } = targetSetupUIStore;

  if (orgTreeLoaded) return;
  if (orgListPromise) return orgListPromise;

  const requestVersion = orgListRequestVersion;
  setLoadingOrgTree(true);

  const currentPromise = (async () => {
    try {
      const data = await organizationService.fetchHierarchy();
      if (requestVersion !== orgListRequestVersion) return;

      const tree = Array.isArray(data?.tree) ? data.tree : [];
      const flatOrgs = flattenOrgTree(tree);
      const list = disambiguateOrgDisplayNames(flatOrgs);

      const user = authSessionStore.user;
      const filteredTree = isSuperAdminRole(user?.role)
        ? stripGroupNodes(tree)
        : (() => {
            const userOrgIds = user?.organizationUnitId || [];
            const allowedSet = new Set(
              userOrgIds.map((id) => normalizeOrgId(id)).filter(Boolean),
            );
            if (user?.assignedToParentCompany && user?.companyId) {
              allowedSet.add(normalizeOrgId(user.companyId));
            }
            return filterOrgTreeByIds(tree, allowedSet);
          })();

      setOrgScope({
        tree,
        list,
        filteredTree,
      });
    } catch {
      if (requestVersion === orgListRequestVersion) {
        showToast.error("Failed to load organizations");
      }
    } finally {
      if (requestVersion === orgListRequestVersion) {
        setLoadingOrgTree(false);
      }
    }
  })();

  orgListPromise = currentPromise.finally(() => {
    if (orgListPromise === currentPromise) {
      orgListPromise = null;
    }
  });

  return orgListPromise;
};

export const loadDisclosureTargets = async (disclosureId, year) => {
  const { setDisclosureTargets, setTargetFrequency } = targetSetupStore;
  const { setDisclosureTargetFrequency, setLoadingTargets } =
    targetSetupUIStore;

  setLoadingTargets(true);
  try {
    const data = await targetSetupService.getTargetsByDisclosure(
      disclosureId,
      year,
    );
    const items = data?.items ?? [];
    setDisclosureTargets(disclosureId, year, items);
    setDisclosureTargetFrequency(
      buildDisclosureYearKey(disclosureId, year),
      data?.targetFrequency ??
        resolveDisclosureTargetFrequency(
          items,
          targetSetupStore.targetFrequency,
        ),
    );
    if (data?.targetFrequency) {
      setTargetFrequency(data.targetFrequency);
    }
  } catch {
    showToast.error("Failed to load targets");
  } finally {
    setLoadingTargets(false);
  }
};

export const saveOrgTarget = async ({
  disclosureId,
  organizationId,
  year,
  targetData,
  existingId,
  disclosure,
  targetFrequency,
}) => {
  const { setOrgTarget } = targetSetupStore;
  const { setSavingTarget } = targetSetupUIStore;

  const validationMessage = getTargetValueValidationMessage(
    disclosure,
    targetData,
    disclosure?.inputType,
  );
  if (validationMessage) {
    showToast.error(validationMessage);
    return null;
  }

  setSavingTarget(true);
  try {
    let result;
    if (existingId) {
      result = await targetSetupService.updateTarget(existingId, {
        targetData,
        targetFrequency,
      });
    } else {
      result = await targetSetupService.createTarget({
        disclosureId,
        organizationId,
        year,
        targetData,
        targetFrequency,
      });
    }
    setOrgTarget(disclosureId, year, organizationId, result);
    showToast.success("Target saved");
    return result;
  } catch (err) {
    showToast.error(err?.message || "Failed to save target");
    return null;
  } finally {
    setSavingTarget(false);
  }
};

export const updateDisclosureTargetFrequency = async ({
  disclosureId,
  year,
  targetFrequency,
}) => {
  const { setDisclosureTargets } = targetSetupStore;
  const { setDisclosureTargetFrequency } = targetSetupUIStore;

  try {
    const result = await targetSetupService.updateMetricFrequency({
      disclosureId,
      year,
      targetFrequency,
    });
    setDisclosureTargets(disclosureId, year, result?.items ?? []);
    setDisclosureTargetFrequency(
      buildDisclosureYearKey(disclosureId, year),
      result?.targetFrequency ?? targetFrequency,
    );
    showToast.success("Target frequency updated");
    return true;
  } catch (err) {
    showToast.error(err?.message || "Failed to update target frequency");
    return false;
  }
};

export const deleteOrgTarget = async ({ id, disclosureId, year, orgId }) => {
  const { removeOrgTarget } = targetSetupStore;
  const { setSavingTarget } = targetSetupUIStore;

  if (!id) return false;

  setSavingTarget(true);
  try {
    await targetSetupService.deleteTarget(id);
    removeOrgTarget(disclosureId, year, orgId);
    showToast.success("Target deleted");
    return true;
  } catch (err) {
    showToast.error(err?.message || "Failed to delete target");
    return false;
  } finally {
    setSavingTarget(false);
  }
};

export const bulkSetValueToAll = async ({
  disclosureId,
  year,
  targetData,
  organizationIds,
  inputType,
  disclosure,
  targetFrequency,
}) => {
  const { setBulkSetting, closeSetValueToAllDialog } = targetSetupUIStore;

  const validationMessage = getTargetValueValidationMessage(
    disclosure,
    targetData,
    inputType,
  );
  if (validationMessage) {
    showToast.error(validationMessage);
    return false;
  }

  setBulkSetting(true);
  try {
    await targetSetupService.bulkCreateTargets({
      disclosureId,
      year,
      targetData,
      organizationIds,
      inputType,
      targetFrequency,
    });
    showToast.success("Targets set for all organizations");
    closeSetValueToAllDialog();
    await loadDisclosureTargets(disclosureId, year);
    return true;
  } catch (err) {
    showToast.error(err?.message || "Failed to set targets");
    return false;
  } finally {
    setBulkSetting(false);
  }
};

export const exportTargetSetupExcel = async ({
  year,
  organizationIds = [],
  frameworks = [],
  categories = [],
  exportMode = "filled",
}) => {
  try {
    const response = await targetSetupService.exportRows({
      year,
      organizationIds,
      frameworks,
      categories,
      exportMode,
    });
    if (!response?.blob) {
      showToast.info("No target setup rows match the selected export mode.");
      return { fileName: null, count: 0 };
    }

    const fileName =
      response.fileName ||
      buildFallbackTargetSetupFileName({
        year,
        exportMode,
      });
    const url = URL.createObjectURL(response.blob);
    const anchor = document.createElement("a");
    anchor.href = url;
    anchor.download = fileName;
    document.body.appendChild(anchor);
    anchor.click();
    anchor.remove();
    URL.revokeObjectURL(url);

    showToast.success("Excel downloaded successfully");
    return { fileName, count: 1 };
  } catch (error) {
    showToast.error(error?.message || "Failed to download Excel");
    return null;
  }
};

export const loadTargetSetupExportSummary = async ({
  year,
  organizationIds = [],
  frameworks = [],
  categories = [],
}) => {
  try {
    return await targetSetupService.getExportSummary({
      year,
      organizationIds,
      frameworks,
      categories,
    });
  } catch {
    return null;
  }
};
